/**
 * One part of a team's data, refreshed from its dataset (shipcast-o7j.3).
 *
 * Shared by the Deliverables and Throughput setup windows: each refreshes its
 * own part — a fresh fetch of the team's dataset, then that part applied to
 * this team only. The panel says where the data comes from, what the last
 * refresh did, and offers the next one.
 *
 * A failed refresh changed nothing, and says so; warnings never block, and
 * stay listed until the next refresh of the same part (Q4).
 */

import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@lean-agility/design-system/react';

import type { ApplyPart, TeamRefreshResponse } from '../types';
import { formatTrackerWarning } from '../warnings';
import { formatAge, useNow } from './staleness';
import type { TeamRefresh } from './teamRefresh';

const PART_NOUN: Record<ApplyPart, string> = {
  deliverables: 'deliverables',
  throughput: 'throughput',
};

export function TeamRefreshPanel({ refresh }: { refresh: TeamRefresh }) {
  const { latest, datasetName, running, part } = refresh;
  const noun = PART_NOUN[part];

  if (datasetName === null) {
    return (
      <p className="text-sm text-text-secondary">
        This team has no data source, so its {noun} are entered by hand. Pick a dataset in the team&apos;s header to
        refresh them from the tracker.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-secondary">
        Data source: <span className="font-medium text-text-primary">{datasetName ?? '…'}</span>{' '}
        <span className="text-xs text-text-muted">(change it in the team&apos;s header)</span>
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={refresh.start} disabled={running}>
          {running ? (
            <Loader2 className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="mr-1.5 size-4" aria-hidden="true" />
          )}
          {running ? 'Refreshing…' : 'Refresh'}
        </Button>
        <p className="text-xs text-text-muted">Fetches the dataset fresh, then updates this team&apos;s {noun} only.</p>
      </div>

      {refresh.startError && (
        <p role="alert" className="text-sm text-feedback-error">
          Could not start the refresh: {refresh.startError.message}
        </p>
      )}

      <LastRefresh latest={latest} noun={noun} />
    </div>
  );
}

function LastRefresh({ latest, noun }: { latest: TeamRefreshResponse | null | undefined; noun: string }) {
  const now = useNow();
  if (latest === undefined || latest?.status === 'running') return null;
  if (latest === null) {
    return <p className="text-sm text-text-muted">Never refreshed from the tracker.</p>;
  }
  const at = latest.completed_at ?? latest.started_at;
  const when = formatAge(now - new Date(at).getTime());

  if (latest.status === 'error') {
    return (
      <p role="alert" className="text-sm text-feedback-error">
        The last refresh ({when}) failed: {latest.error_message ?? 'unknown error'}. The team&apos;s {noun} were left as
        they were.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-sm text-feedback-success">
        Refreshed {when}
        {latest.deliverables_upserted != null &&
          ` · ${latest.deliverables_upserted} deliverables updated, ${latest.dependencies_inserted ?? 0} dependencies added`}
        {latest.warnings.length > 0 && ` · ${latest.warnings.length} warning${latest.warnings.length === 1 ? '' : 's'}`}
        .
      </p>
      {latest.warnings.length > 0 && (
        <ul aria-label="Warnings from the last refresh" className="list-disc space-y-0.5 pl-5">
          {latest.warnings.map((w, i) => (
            <li key={`${w.kind}-${i}`} className="text-xs text-feedback-warning">
              {formatTrackerWarning(w)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
